// laneSpeeds.js — build a lane's speedSegments from per-point LRS enrichment.
//
// Platform-native replacement for the offline zone-paste flow (project 2468,
// `speed+miles Massdot.py`): instead of pasting the state service's
// From_Measure/To_Measure milepost ranges into the XML wholesale — which
// (a) applied ONE direction's zones to both directions and (b) inherited the
// endpoint-linearization drift of up to ±390 ft — the zones are derived from
// the lane's OWN densely-sampled points (~10 ft apart), each already carrying
// the speed fields of the LRS section it physically projects onto
// (`enrichPointsWithLrs`: `speed_limit` = posted sign inventory,
// `speed_limit_regulatory` = Special Speed Regulation). Boundaries land on the
// lane's arc axis at ~10 ft resolution, per direction, by construction.
//
// Field choice is PER ADAPTER (`capabilities.speedFieldPreference`), because the
// two records mean different things and states differ in which one is
// authoritative:
//   * 'posted' (DEFAULT, unchanged for every adapter that does not opt out) —
//     `speed_limit` first, regulatory as fallback. The analysis's tested speed
//     is validated against physical signage (street view), and posted is the
//     signs; regulatory fills the majority of segments where the sign inventory
//     is empty.
//   * 'regulatory' (MassDOT, 2026-08-25) — `speed_limit_regulatory` first,
//     posted as fallback. MassDOT's Special Speed Regulation is the legally
//     enforceable limit and the one their engineers audit against; their posted
//     sign inventory is sparse (Speed_Lim populated on ~18% of segments).
// Either way the OTHER field is still the fallback, so a point with only one
// record still yields a speed instead of a gap.
//
// The two fields' boundaries can disagree by a few hundred feet (see massdot.js
// "two speed records" note); the run smoothing below suppresses the short
// double-step that mixing them can produce.

import { smoothRouteRuns } from './lrsMath';

const EARTH_RADIUS_M = 6371000;
const METERS_TO_MILES = 0.000621371;

function haversineMeters(a, b) {
  const dLat = ((b.lat - a.lat) * Math.PI) / 180;
  const dLng = ((b.lng - a.lng) * Math.PI) / 180;
  const s =
    Math.sin(dLat / 2) ** 2 +
    Math.cos((a.lat * Math.PI) / 180) * Math.cos((b.lat * Math.PI) / 180) *
    Math.sin(dLng / 2) ** 2;
  return 2 * EARTH_RADIUS_M * Math.atan2(Math.sqrt(s), Math.sqrt(1 - s));
}

/** Cumulative arc miles along `[{lat,lng}, …]` in order; [0] === 0. */
export function cumulativeMilesFromLatLng(points) {
  const pts = Array.isArray(points) ? points : [];
  const out = new Array(pts.length).fill(0);
  for (let i = 1; i < pts.length; i += 1) {
    out[i] = out[i - 1] + haversineMeters(pts[i - 1], pts[i]) * METERS_TO_MILES;
  }
  return out;
}

export const SPEED_FIELD_POSTED = 'posted';
export const SPEED_FIELD_REGULATORY = 'regulatory';

/** Per-point speed choice. `preference` picks which of the two MassDOT-style
 *  records leads; the other is always the fallback, so a point carrying only
 *  one of them still yields a speed. Defaults to POSTED — every adapter that
 *  does not opt in to 'regulatory' behaves exactly as before.
 *  Null when the point has no LRS hit or neither field has a usable value. */
export function speedForLrsPoint(lrs, preference = SPEED_FIELD_POSTED) {
  if (!lrs) return null;
  const posted = Number(lrs.speed_limit);
  const reg = Number(lrs.speed_limit_regulatory);
  const usable = (v) => Number.isFinite(v) && v > 0;
  const [first, second] = preference === SPEED_FIELD_REGULATORY
    ? [reg, posted]
    : [posted, reg];
  if (usable(first)) return first;
  if (usable(second)) return second;
  return null;
}

/** Fill null gaps from the nearest non-null neighbour (forward pass, then a
 *  backward pass for a leading gap). Returns a new array; all-null stays
 *  all-null. */
function fillGaps(speeds) {
  const out = [...speeds];
  let last = null;
  for (let i = 0; i < out.length; i += 1) {
    if (out[i] == null) out[i] = last;
    else last = out[i];
  }
  let next = null;
  for (let i = out.length - 1; i >= 0; i -= 1) {
    if (out[i] == null) out[i] = next;
    else next = out[i];
  }
  return out;
}

/**
 * Build `speedSegments` ([{ mileMark, speedMph }], the same shape the manual
 * speed-edit tool produces) from per-point LRS enrichment.
 *
 * @param lrsPerPoint  `enrichPointsWithLrs` result — one entry (or null) per
 *                     lane point, in drawn order.
 * @param arcMiles     cumulative miles along the lane at the SAME points
 *                     (drawn order, arcMiles[0] === 0).
 * @param minRun       smoothing threshold in points (default: lrsMath's 50,
 *                     ~500 ft at 10-ft sampling) — interior runs shorter than
 *                     this with agreeing neighbours are treated as artefacts.
 * @returns segments in the lane's arc axis; [] when no point carries a speed
 *          (caller keeps the manual/default behaviour).
 */
export function buildSpeedSegmentsFromLrs(
  lrsPerPoint,
  arcMiles,
  { minRun, speedFieldPreference } = {},
) {
  const pts = Array.isArray(lrsPerPoint) ? lrsPerPoint : [];
  const miles = Array.isArray(arcMiles) ? arcMiles : [];
  const n = Math.min(pts.length, miles.length);
  if (n < 2) return [];

  // Undefined preference => POSTED, so callers that pass nothing (and every
  // adapter without `capabilities.speedFieldPreference`) are unchanged.
  const pref = speedFieldPreference || SPEED_FIELD_POSTED;
  const raw = [];
  for (let i = 0; i < n; i += 1) raw.push(speedForLrsPoint(pts[i], pref));
  if (!raw.some((s) => s != null)) return [];

  const filled = fillGaps(raw);
  const smoothed = smoothRouteRuns(filled, minRun ? { minRun } : undefined);

  // Per-point posted milepost from the same enrichment — carried onto each
  // segment so the GUI can label LRS-loaded zones in mile posts instead of
  // lane arc miles. Display-only: the XML writer keys on mileMark.
  const mpAt = (i) => {
    const raw = pts[i]?.milepost;
    if (raw == null) return null; // Number(null) === 0 — never invent MP 0
    const v = Number(raw);
    return Number.isFinite(v) ? Math.round(v * 10000) / 10000 : null;
  };
  const mpBetween = (a, b) => {
    const ma = mpAt(a);
    const mb = mpAt(b);
    if (ma != null && mb != null) return Math.round(((ma + mb) / 2) * 10000) / 10000;
    return mb ?? ma;
  };

  const segments = [{ mileMark: 0, speedMph: smoothed[0], milepost: mpAt(0) }];
  for (let i = 1; i < n; i += 1) {
    if (smoothed[i] === smoothed[i - 1]) continue;
    // Boundary at the midpoint between the last point of the old zone and the
    // first point of the new one (~5 ft placement error at 10-ft sampling).
    const mid = (Number(miles[i - 1]) + Number(miles[i])) / 2;
    segments.push({
      mileMark: Math.round(mid * 10000) / 10000,
      speedMph: smoothed[i],
      milepost: mpBetween(i - 1, i),
    });
  }
  // The final zone's far end has no "next segment" to carry its milepost.
  segments[segments.length - 1].milepostEnd = mpAt(n - 1);
  return segments;
}

export default buildSpeedSegmentsFromLrs;
